import { ReactNode, createContext, useContext, useMemo } from 'react'

import { RespondentNoun } from './respondentNoun'

export { cleanSegmentLabel, respondentNoun, withSegment } from './respondentNoun'
export type { RespondentNoun } from './respondentNoun'

/**
 * What the shared quick-study charts call the people who answered.
 *
 * The Studies report keeps "twins" (the default, so nothing outside a thread
 * changes). Thread result surfaces wrap themselves in a provider set to
 * "respondents" (owner decision for the quick-study surface only), and may add
 * the run's segment label so a filtered run's base footnote names who was
 * asked: "Base: 40 respondents, women 21 to 34".
 *
 * The chart copy here is the legacy hardcoded English the components already
 * render; this only swaps the noun, it does not localize them.
 */
export interface QuickStudyNounValue {
  noun: RespondentNoun
  /** The run's segment label, when the run was drawn from a filtered segment. */
  segmentLabel?: string | null
}

const DEFAULT_VALUE: QuickStudyNounValue = { noun: 'twins' }

const QuickStudyNounContext = createContext<QuickStudyNounValue>(DEFAULT_VALUE)

interface QuickStudyNounProviderProps {
  noun?: RespondentNoun
  /**
   * `undefined` inherits the enclosing provider's label; `null` clears it.
   */
  segmentLabel?: string | null
  children: ReactNode
}

export function QuickStudyNounProvider(props: QuickStudyNounProviderProps) {
  const { noun, segmentLabel, children } = props

  const parent = useContext(QuickStudyNounContext)

  const value = useMemo<QuickStudyNounValue>(
    () => ({
      noun: noun ?? parent.noun,
      segmentLabel: segmentLabel === undefined ? parent.segmentLabel : segmentLabel,
    }),
    [noun, segmentLabel, parent.noun, parent.segmentLabel],
  )

  return (
    <QuickStudyNounContext.Provider value={value}>
      {children}
    </QuickStudyNounContext.Provider>
  )
}

export function useQuickStudyNoun(): QuickStudyNounValue {
  return useContext(QuickStudyNounContext)
}
